import React, { Fragment, useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    CardMedia,
    Avatar,
    Tab,
    Tabs,
    Breadcrumbs,
} from "@mui/material";
import { useParams } from "react-router-dom";
import CartModel from "../components/common/CartModel";
import Layout from "../components/layouts/Layout";
import { useSearch } from "../context/SearchContext";

const VendorInfoPage = () => {
    const { id } = useParams();
    const [tabValue, setTabValue] = useState(0);
    const [selectedFood, setSelectedFood] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedVendor, setVendor] = useState(null);
    const [allProducts, setAllProducts] = useState([]);  // State for "All" products
    const [prevId, setPrevId] = useState(null);

    const { get_vendor_by_id, get_products_for_vendor } = useSearch();  // Assume you have this function

    // Fetch vendor data on initial load
    useEffect(() => {
        if (id === prevId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        get_vendor_by_id(id)
            .then((response) => {
                setVendor(response.data);
                setPrevId(id);
                fetchAllProducts(response.data.id);  // Fetch All products after vendor info is loaded
            })
            .catch((error) => {
                console.error("Error fetching vendor data:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, get_vendor_by_id, prevId]);

    // Fetch "All" products when vendor info is loaded
    const fetchAllProducts = (vendorId) => {
        get_products_for_vendor(vendorId)
            .then((response) => {
                setAllProducts(response);
            })
            .catch((error) => {
                console.error("Error fetching all products:", error);
            });
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleOpenModal = (food) => {
        setSelectedFood(food);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    if (loading) {
        return (
            <Layout title={"Loading Vendor Info"}>
                <Box sx={{ padding: "20px", textAlign: "center" }}>
                    <Typography variant="caption">Loading vendor details...</Typography>
                </Box>
            </Layout>
        );
    }

    if (!selectedVendor) {
        return (
            <Layout title={"Vendor Not Found"}>
                <Box sx={{ padding: "20px", textAlign: "center" }}>
                    <Typography variant="h6">Vendor not found.</Typography>
                </Box>
            </Layout>
        );
    }

    return (
        <Fragment>
            <Layout title={`Vendor - ${selectedVendor.name}`}>
                <Breadcrumbs title={selectedVendor.name} crumb="Food" />
                <Box sx={{ padding: "20px", backgroundColor: "#f9f9f9" }}>
                    {/* Header */}
                    <Box
                        sx={{
                            backgroundColor: "#dd4a48",
                            padding: { xs: "16px", sm: "20px" },
                            color: "#fff",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: { xs: "center", sm: "space-between" },
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar
                                sx={{
                                    backgroundColor: "#fff",
                                    color: "#673ab7",
                                    width: { xs: 60, sm: 70 },
                                    height: { xs: 60, sm: 70 },
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    alt="logo"
                                    image={selectedVendor.logo || "https://via.placeholder.com/140"}
                                    sx={{
                                        objectFit: "cover",
                                        height: "100%",
                                        width: "100%",
                                        borderRadius: "10px",
                                    }}
                                />
                            </Avatar>
                            <Box>
                                <Typography variant="button" fontWeight="bold" sx={{ fontSize: { xs: "1.2rem", sm: "1.2rem" } }}>
                                    {selectedVendor.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    dangerouslySetInnerHTML={{ __html: selectedVendor.description }}
                                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                                />
                                <Typography variant="caption" color="inherit" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                    {selectedVendor.address}
                                </Typography>
                            </Box>
                        </Box>
                        <Box textAlign="right">
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}>
                                {selectedVendor.max_order} Max Order
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                <span>⭐ {selectedVendor.rating}</span> | {selectedVendor.reviews} Reviews
                            </Typography>
                        </Box>
                    </Box>

                    {/* Tabs */}
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{ marginTop: "20px" }}
                        indicatorColor="primary"
                        textColor="primary"
                        centered
                    >
                        <Tab label="All" /> {/* All Tab */}
                        {selectedVendor.menus.map((menu, index) => (
                            <Tab key={index} label={menu.name} />
                        ))}
                    </Tabs>

                    {/* Product List */}
                    <Grid container spacing={3} sx={{ marginTop: "20px" }}>
                        {tabValue === 0
                            ? allProducts.map((food, index) => ( // Render "All" products if "All" tab is selected
                                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                    <Box
                                        onClick={() => handleOpenModal(food)}
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            padding: "16px",
                                            textAlign: "center",
                                            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                                            width: "100%",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <img
                                            src={food.photo}
                                            alt={food.name}
                                            style={{
                                                width: "65%",
                                                height: "100px",
                                                objectFit: "cover",
                                                borderRadius: "8px",
                                                marginBottom: "8px",
                                            }}
                                        />
                                        <Typography variant="body1" fontWeight="bold">
                                            {food.name}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            Kshs. {food.sell_price.toFixed(2)}
                                        </Typography>
                                        {(
                                            <CartModel
                                                testOne={() => { }}
                                                open={modalOpen}
                                                onClose={handleCloseModal}
                                                title={food.name}
                                                item={food}
                                                is_restro_open={true}
                                                minimum_order_quantity={1}
                                                total_allowed_quantity={10}
                                                product_qty={1}
                                                type={0}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            ))
                            : selectedVendor.menus[tabValue - 1]?.products.map((food, index) => ( // Render products for the selected menu
                                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                    <Box
                                        onClick={() => handleOpenModal(food)}
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            padding: "16px",
                                            textAlign: "center",
                                            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                                            width: "100%",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <img
                                            src={food.photo}
                                            alt={food.name}
                                            style={{
                                                width: "65%",
                                                height: "100px",
                                                objectFit: "cover",
                                                borderRadius: "8px",
                                                marginBottom: "8px",
                                            }}
                                        />
                                        <Typography variant="body1" fontWeight="bold">
                                            {food.name}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            Kshs. {food.sell_price.toFixed(2)}
                                        </Typography>
                                        {(
                                            <CartModel
                                                testOne={() => { }}
                                                open={modalOpen}
                                                onClose={handleCloseModal}
                                                title={food.name}
                                                item={food}
                                                is_restro_open={true}
                                                minimum_order_quantity={1}
                                                total_allowed_quantity={10}
                                                product_qty={1}
                                                type={0}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                    </Grid>
                </Box>
            </Layout>
        </Fragment>
    );
};

export default VendorInfoPage;
